/**
 * Character tables that reproduce the seed's JS regex semantics without a regex engine.
 *
 * The seed's `\b` and `\w` are ASCII-word based, `\s` is the engine's whitespace class, and the
 * `i` flag (no `u`) folds ASCII letters only (a non-ASCII code unit never matches an ASCII one).
 * ENGINE-SPEC §5 (`\b` parity); differences on non-ASCII letters are C-LEX-UNI.
 */

/** 1 where the UTF-16 code unit is JS `\s`. Derived from the running engine, not hand-listed. */
export const IS_SPACE: Uint8Array = (() => {
	const t = new Uint8Array(65536);
	const re = /\s/;
	for (let c = 0; c < 65536; c++) if (re.test(String.fromCharCode(c))) t[c] = 1;
	return t;
})();

/** JS `\w`: [A-Za-z0-9_]. */
export function isWordCode(c: number): boolean {
	return (c >= 97 && c <= 122) || (c >= 65 && c <= 90) || (c >= 48 && c <= 57) || c === 95;
}

/** ASCII fold used by the `i` flag: A-Z to a-z, everything else unchanged. */
export function foldAscii(c: number): number {
	return c >= 65 && c <= 90 ? c + 32 : c;
}

/** Lower-case a string by ASCII fold only (what `/x/i` sees; never changes the length). */
export function foldAsciiString(s: string): string {
	let out = '';
	let last = 0;
	for (let i = 0; i < s.length; i++) {
		const c = s.charCodeAt(i);
		if (c >= 65 && c <= 90) {
			out += s.slice(last, i) + String.fromCharCode(c + 32);
			last = i + 1;
		}
	}
	return last === 0 ? s : out + s.slice(last);
}

/**
 * Per code unit `String.prototype.toLowerCase` for the seed tokenizer (`text.toLowerCase()`
 * followed by a word regex). 0 marks a code unit whose lower case is not one code unit (U+0130).
 */
export const LOWER: Uint16Array = (() => {
	const t = new Uint16Array(65536);
	for (let c = 0; c < 65536; c++) {
		const l = String.fromCharCode(c).toLowerCase();
		t[c] = l.length === 1 ? l.charCodeAt(0) : 0;
	}
	return t;
})();
